/**
 * Vote Button for Gender Party
 * Two large buttons for BOY/GIRL voting with haptic feedback
 */

import { useCallback } from "react";
import type { GenderVote } from "../hooks";
import styles from "./VoteButton.module.css";

interface VoteButtonProps {
  vote: GenderVote;
  isSelected?: boolean;
  disabled?: boolean;
  onVote: (vote: GenderVote) => void;
  className?: string;
}

function triggerHaptic() {
  if ("vibrate" in navigator) {
    navigator.vibrate(50);
  }
}

export function VoteButton({
  vote,
  isSelected = false,
  disabled = false,
  onVote,
  className = "",
}: VoteButtonProps) {
  const handleClick = useCallback(() => {
    if (disabled) return;
    triggerHaptic();
    onVote(vote);
  }, [vote, disabled, onVote]);

  const isBoy = vote === "BOY";

  return (
    <button
      type="button"
      className={`${styles.button} ${isBoy ? styles.boy : styles.girl} ${
        isSelected ? styles.selected : ""
      } ${disabled ? styles.disabled : ""} ${className}`}
      onClick={handleClick}
      disabled={disabled}
      aria-pressed={isSelected}
    >
      <span className={styles.emoji}>{isBoy ? "👦" : "👧"}</span>
      <span className={styles.label}>{isBoy ? "Мальчик" : "Девочка"}</span>
      {isSelected && <span className={styles.checkmark}>✓</span>}
    </button>
  );
}

interface VoteButtonsProps {
  myVote?: GenderVote | null;
  disabled?: boolean;
  allowVoteChange?: boolean;
  onVote: (vote: GenderVote) => void;
  className?: string;
}

export function VoteButtons({
  myVote,
  disabled = false,
  allowVoteChange = true,
  onVote,
  className = "",
}: VoteButtonsProps) {
  const hasVoted = myVote !== null && myVote !== undefined;
  const canChangeVote = allowVoteChange || !hasVoted;

  return (
    <div className={`${styles.buttonsContainer} ${className}`}>
      <VoteButton
        vote="BOY"
        isSelected={myVote === "BOY"}
        disabled={disabled || !canChangeVote}
        onVote={onVote}
      />
      <VoteButton
        vote="GIRL"
        isSelected={myVote === "GIRL"}
        disabled={disabled || !canChangeVote}
        onVote={onVote}
      />
    </div>
  );
}
